import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Users, Buildings, Clock, Warning, Storefront } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard({ filialen, medewerkers, loading }) {
  const [aanwezigVandaag, setAanwezigVandaag] = useState(0);
  const [teLaatVandaag, setTeLaatVandaag] = useState(0);
  const [kassaOpen, setKassaOpen] = useState({ stuks: 0, regels: 0 });
  const [kassaPerFiliaal, setKassaPerFiliaal] = useState({});

  useEffect(() => {
    axios.get(`${API}/dashboard`).then((res) => {
      setAanwezigVandaag(res.data.aanwezig_vandaag || 0);
      setTeLaatVandaag(res.data.te_laat_vandaag || 0);
      setKassaOpen({ stuks: res.data.kassa_open_stuks || 0, regels: res.data.kassa_open_regels || 0 });
      const perF = {};
      (res.data.per_filiaal || []).forEach((f) => { perF[f.naam] = f.kassa_open_stuks || 0; });
      setKassaPerFiliaal(perF);
    }).catch(() => {});
  }, []);

  const totaalMedewerkers = medewerkers.length;
  const totaalFilialen = filialen.length;

  const filiaalMap = {};
  filialen.forEach((f) => { filiaalMap[f.id] = { naam: f.naam, aantal: 0 }; });
  medewerkers.forEach((m) => {
    (m.filiaal_ids || []).forEach((fid) => {
      if (filiaalMap[fid]) filiaalMap[fid].aantal += 1;
    });
  });
  const perFiliaal = Object.values(filiaalMap);
  const maxAantal = Math.max(...perFiliaal.map((f) => f.aantal), 1);

  if (loading) {
    return (
      <div data-testid="dashboard-loading" className="space-y-6">
        <div className="h-8 w-48 bg-muted animate-pulse rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (<div key={i} className="h-28 bg-muted animate-pulse rounded-sm" />))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="page-title">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Overzicht van uw personeelsbestand</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <div className="stat-card" data-testid="stat-medewerkers">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center">
              <Users size={20} weight="bold" className="text-primary" />
            </div>
            <span className="section-label">Medewerkers</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{totaalMedewerkers}</p>
        </div>

        <div className="stat-card" data-testid="stat-filialen">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-primary/10 flex items-center justify-center">
              <Buildings size={20} weight="bold" className="text-primary" />
            </div>
            <span className="section-label">Filialen</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{totaalFilialen}</p>
        </div>

        <div className="stat-card" data-testid="stat-aanwezig">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-[#38A169]/10 flex items-center justify-center">
              <Clock size={20} weight="bold" className="text-[#38A169]" />
            </div>
            <span className="section-label">Aanwezig Vandaag</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{aanwezigVandaag}</p>
        </div>

        <div className="stat-card" data-testid="stat-te-laat">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-[#E53E3E]/10 flex items-center justify-center">
              <Warning size={20} weight="bold" className="text-[#E53E3E]" />
            </div>
            <span className="section-label">Te Laat Vandaag</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{teLaatVandaag}</p>
        </div>

        <Link to="/kassa" className="stat-card hover:border-[#002FA7] transition-colors" data-testid="stat-kassa-open">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
              <Storefront size={20} weight="bold" className="text-[#002FA7]" />
            </div>
            <span className="section-label">Uit Stock Halen</span>
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>{kassaOpen.stuks}</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            {kassaOpen.regels} regel{kassaOpen.regels === 1 ? "" : "s"} verkocht
          </p>
        </Link>
      </div>

      {/* Per Filiaal */}
      <div>
        <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Per Filiaal</h2>
        {perFiliaal.length === 0 ? (
          <div className="bg-white border border-border rounded-sm p-8 text-center text-muted-foreground" data-testid="no-filialen-message">
            <Buildings size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">Nog geen filialen toegevoegd</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="filiaal-overview-grid">
            {perFiliaal.map((f, i) => (
              <div key={i} className="bg-white border border-border rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm">{f.naam}</h3>
                  <div className="flex items-center gap-1.5">
                    {kassaPerFiliaal[f.naam] > 0 && (
                      <Badge className="text-xs bg-[#002FA7] text-white" data-testid={`filiaal-kassa-${i}`}>
                        {kassaPerFiliaal[f.naam]} uit stock
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-xs">
                      {f.aantal} {f.aantal === 1 ? "medewerker" : "medewerkers"}
                    </Badge>
                  </div>
                </div>
                <div className="w-full h-2 bg-muted rounded-sm overflow-hidden">
                  <div className="h-full bg-primary rounded-sm transition-all duration-500" style={{ width: `${(f.aantal / maxAantal) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
